import React from 'react'
import PropTypes from 'prop-types'


/**
 * Reusable toggle switch component based on Bootstrap 5 toggle (future-proof!)
 * Can be used as controlled or uncontrolled component
 *
 * @component
 * @param {string} [size='medium'] - Size of the toggle switch ('small', 'medium', 'large')
 * @param {boolean} [defaultChecked=false] - Default checked state for uncontrolled use
 * @param {boolean} [checked] - Checked state for controlled usage (makes component controlled)
 * @param {Function} [onChange] - Callback fn fired when toggle state changes
 * @param {boolean} [disabled=false] - Whether input is disabled (hard disable, no user interaction)
 * @param {string} id - Required id links input to label
 * @param {string} [name] - Name attribute for form submission
 * @param {...Object} props -  Optional props
 *
 * @example
 * // Uncontrolled usage
 * <div className="toggle-form-group">
 *   <ToggleSwitch id="show-legend" size="medium" defaultChecked={true} />
 *   <label htmlFor="show-legend" className="form-label">Show Legend on Map</label>
 * </div>
 *
 * @example
 * // Controlled usage with state management
 * const [isLegendVisible, setIsLegendVisible] = useState(false)
 *
 * <div className="toggle-form-group">
 *   <ToggleSwitch
 *     id="show-legend"
 *     size="large"
 *     checked={isEnabled}
 *     onChange={(e) => setIsEnabled(e.target.checked)}
 *   />
 *   <label htmlFor="show-legend" className="form-label">Show Legend on Map</label>
 * </div>
 */
const ToggleSwitch = ({
  size = 'medium',
  defaultChecked = false,
  checked,
  onChange,
  disabled = false,
  id,
  name,
  ...props
}) => {
  const isControlled = checked !== undefined

  return (
    <div className={`toggle-switch toggle-switch-${size}`}>
      <input
        type="checkbox"
        className="toggle-switch-input shadow-gl-light"
        id={id}
        name={name}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        checked={isControlled ? checked : undefined}
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
    </div>
  )
}

ToggleSwitch.propTypes = {
  size:           PropTypes.oneOf(['small', 'medium', 'large']),
  defaultChecked: PropTypes.bool,
  checked:        PropTypes.bool,
  onChange:       PropTypes.func,
  disabled:       PropTypes.bool,
  id:             PropTypes.string.isRequired,
  name:           PropTypes.string,
}

export default ToggleSwitch